import { type FunctionComponent } from 'react';
import { Loading } from '../../common-components/loading/loading.component';
import { BaseButton, type BaseButtonProps } from '../base-button/base-button.component';
import { type ButtonSize, type ButtonStyle } from '../button.types';
import { chooseClasses } from './styled-button.helper';
/**
 * StyledButton takes all props base button does except `className` and narrows
 * the `size` prop to not accept null.  This is to reduce inconsistency.
 */
export type StyledButtonProps = {
	buttonStyle?: Extract<
		ButtonStyle,
		| 'PRIMARY'
		| 'PRIMARY_ACCENT'
		| 'SECONDARY'
		| 'INTERNAL'
		| 'DANGER'
		| 'INTERNAL_SECONDARY'
		| 'TERTIARY'
		| 'INTERNAL_TERTIARY'
		| 'DESTRUCTIVE_PRIMARY'
		| 'DESTRUCTIVE_SECONDARY'
		| 'DESTRUCTIVE_TERTIARY'
	>;
	size?: NonNullable<Extract<ButtonSize, 'DEFAULT' | 'SMALL' | 'LARGE'>>;
	isBusy?: boolean;
	busyText?: string;
} & Omit<BaseButtonProps, 'className' | 'size'>;

export const StyledButton: FunctionComponent<StyledButtonProps> = ({
	buttonStyle = 'PRIMARY',
	isBusy,
	busyText = '',
	children,
	disabled,
	...remainingProps
}) => {
	if (buttonStyle === 'PRIMARY_ACCENT') {
		// Note: This is done so the text can be animated on click
		children = <div data-testid="primary-accent-content">{children}</div>;
	}
	const busyContent = (
		<div className="justify-center flex gap-2 items-center">
			<span className="hidden items-center theme-luxury:flex!">
				<Loading />
			</span>
			<span>{busyText}</span>
		</div>
	);
	const isDisabled = disabled || isBusy;
	const classes = chooseClasses(buttonStyle, isBusy, disabled);
	return (
		<BaseButton disabled={isDisabled} className={classes} {...remainingProps}>
			{isBusy ? busyContent : children}
		</BaseButton>
	);
};
